import { useSuspenseQuery } from '@tanstack/react-query';
import { MutableRefObject } from 'react';
import { MapRef } from 'react-map-gl';

import { getRequest } from '@/api/rest/hooks/requests/get';
import { MAP_LAYER_FACTS } from '@/api/rest/resources/types/fact';
import {
  GET_GEOSERVER_LAYER_FEATURES,
  GetGeoserverLayerFeaturesErrorDto,
  GetGeoserverLayerFeaturesSuccessDto,
} from '@/api/rest/resources/types/geoserver';
import { LAYER_OPTIONS } from '@/components/widgets/o/Map/constants';
import { IUCN_STATIC_HASH } from '@/config/constants';
import { assertIsDefined } from '@/utils/assert';

import { useMapEndpointPlot } from './useMapEndpoint';

const DEFAULT_QUERY_PARAMS = {
  service: 'WMS',
  version: '1.1.1',
  request: 'GetFeatureInfo',
  format: 'image/png',
  transparent: 'true',
  info_format: 'application/json',
  srs: 'EPSG:4326',
};

type SuccessDto = GetGeoserverLayerFeaturesSuccessDto;
type ErrorDto = GetGeoserverLayerFeaturesErrorDto;

const FEATURE_INFO_BUFFER_PIXELS = 10;

type GetFeatureInfoBaseProps = {
  longitude: number;
  latitude: number;
  mapRef: MutableRefObject<MapRef | null>;
  queryParamsArg?: Partial<typeof DEFAULT_QUERY_PARAMS>;
  year: string;
};

type UseGetFeatureInfoProps = {
  layer: string;
} & GetFeatureInfoBaseProps;

export const useGetFeatureInfo = ({
  layer,
  longitude,
  latitude,
  mapRef,
  year,
  queryParamsArg = DEFAULT_QUERY_PARAMS,
}: UseGetFeatureInfoProps) => {
  const layerInfo = LAYER_OPTIONS.find((option) => option.value === layer);
  let layerOrigin = 'landscape';
  const canvas = mapRef.current?.getCanvas();
  const bounds = mapRef.current?.getBounds();

  assertIsDefined(layerInfo, 'layerInfo');
  assertIsDefined(canvas, 'canvas');
  assertIsDefined(bounds, 'bounds');

  const mapUrl = useMapEndpointPlot(layerInfo, year);

  // Use a tight bbox around the click coordinate so GeoServer can identify features precisely regardless of zoom level
  const degPerPixelX = (bounds.getEast() - bounds.getWest()) / canvas.width;
  const degPerPixelY = (bounds.getNorth() - bounds.getSouth()) / canvas.height;
  const pixelSize = FEATURE_INFO_BUFFER_PIXELS * 2;
  const bbox = [
    longitude - FEATURE_INFO_BUFFER_PIXELS * degPerPixelX,
    latitude - FEATURE_INFO_BUFFER_PIXELS * degPerPixelY,
    longitude + FEATURE_INFO_BUFFER_PIXELS * degPerPixelX,
    latitude + FEATURE_INFO_BUFFER_PIXELS * degPerPixelY,
  ].join(',');

  let layerName = layerInfo.isTimeSeries
    ? `${layerInfo.origin}:${layerInfo.value}_${year}`
    : `${layerInfo.origin}:${layerInfo.value}`;

  // SUPER HACK: to make iucn url unguessable. to be removed when keycloak is setup on geoserver
  if (layerInfo.value === MAP_LAYER_FACTS.iucn_protected_areas) {
    layerName = `${layerName}_${IUCN_STATIC_HASH}`;
  }

  if (
    [
      MAP_LAYER_FACTS.combined_ibat_lulc_urls,
      MAP_LAYER_FACTS.iucn_get_level_2_biome_raster_asset,
      MAP_LAYER_FACTS.iucn_get_level_3_efg_raster_asset,
    ].includes(layerInfo.value)
  ) {
    const queryParams = new URLSearchParams(mapUrl);
    layerName = queryParams.get('layers') ?? '';
    layerOrigin = layerName.split(':')[0] ?? 'landscape';
  }

  const queryParams = {
    ...queryParamsArg,
    query_layers: layerName,
    layers: layerName,
    x: FEATURE_INFO_BUFFER_PIXELS,
    y: FEATURE_INFO_BUFFER_PIXELS,
    width: pixelSize,
    height: pixelSize,
    bbox,
  };

  const pathVariables = {
    map_origin: layerOrigin,
  };

  const queryFn = async () => {
    return getRequest<SuccessDto>(GET_GEOSERVER_LAYER_FEATURES, pathVariables, queryParams);
  };

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: ['geoserver', 'map', queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};
